import { createApi } from "@reduxjs/toolkit/query/react";
import { API_BASE_URL } from "../../utils/constants";
import { axiosBaseQuery } from "../baseQuery";

import userParser from "../parser/userParser";

import {
  FetchUserRequest,
  FetchUsersResponse,
  ParsedFetchUserResponse,
  CreateUserRequest,
  CreateUserResponse,
  EditUserRequest,
  EditUserResponse,
  FetchRolesResponse,
  ParsedRoleResponse,
  ParsedProgramResponse,
  FetchProgramResponse,
  updateUserStatusPayload,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ForgotPasswordResponse,
  ParseProgramTypeResponse,
  FetchProgramTypeResponse,
  // ParseProjectData,
  FetchProjectResponse,
} from "../types/user";

export const userApi: any = createApi({
  reducerPath: "userApi",
  baseQuery: axiosBaseQuery({ baseUrl: API_BASE_URL }),
  tagTypes: ["USER", "ROLE"],

  endpoints: (builder) => ({
    sendOtp: builder.mutation<void, { email: string }>({
      query: (data: any) => ({
        url: "/user/send-otp",
        method: "POST",
        data,
      }),
    }),

    verifyOtp: builder.mutation<void, { email: string; otp: string }>({
      query: (data: any) => ({
        url: "/user/verify-otp",
        method: "POST",
        data,
      }),
    }),

    fetchedUsers: builder.query<ParsedFetchUserResponse, FetchUserRequest>({
      query: (params: any) => ({
        url: `/user`,
        method: "GET",
        params: params,
      }),
      providesTags: ["USER"],

      transformResponse: (res: FetchUsersResponse) => {
        return userParser.fetchedUsers(res);
      },
    }),

    userRoles: builder.query<ParsedRoleResponse[], any>({
      query: (params: any) => ({
        url: `/role/list`,
        method: "GET",
        params: params,
      }),
      providesTags: ["ROLE"],
      transformResponse: (response: FetchRolesResponse) =>
        userParser.userRoles(response),
    }),

    //master program type
    userProgram: builder.query<ParsedProgramResponse[], void>({
      query: () => ({
        url: `/program-master/list`,
        method: "GET",
      }),
      transformResponse: (response: FetchProgramResponse) => {
        return userParser.userProgram(response);
      },
    }),

    //list of program type
    listOfPrograms: builder.query<ParseProgramTypeResponse[], void>({
      query: (params?: any) => ({
        url: `/program-type/list`,
        method: "GET",
        params: params,
      }),
      providesTags: ["USER"],
      transformResponse: (response: FetchProgramTypeResponse) =>
        userParser.userProgramType(response),
    }),

    // List Of Project
    listOfProjects: builder.query<any[], void>({
      query: (params?: any) => ({
        url: `/scope/scope-parents`,
        method: "GET",
        params: params,
      }),
      providesTags: ["USER"],
      transformResponse: (response: FetchProjectResponse) =>
        userParser.userProjects(response),
    }),

    createRole: builder.mutation({
      query: (payload: any) => ({
        url: `/role`,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["ROLE"],
    }),

    getRolesById: builder.query({
      query: (roleId: any) => ({
        url: `/role`,
        method: "GET",
        params: { roleId },
      }),
      transformResponse: (response: any) =>
        userParser.getRolesByIdParser(response),
    }),

    createProject: builder.mutation({
      query: (payload: any) => ({
        url: `/scope/scope-parent`,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["USER", "ROLE"], // Adjust tags as necessary
    }),

    editProject: builder.mutation({
      query: ({ payload, scopeParentName }) => ({
        url: `/scope/scope-parent/${scopeParentName}`,
        method: "PUT",
        data: payload,
      }),
      invalidatesTags: ["USER", "ROLE"],
    }),

    deleteProject: builder.mutation({
      query: (scopeParentName) => ({
        url: `/scope/scope-parent`,
        method: "DELETE",
        params: scopeParentName,
      }),
      invalidatesTags: ["USER", "ROLE"],
    }),

    createUser: builder.mutation({
      query: (payload: any) => {
        return {
          url: `/user`,
          method: "POST",
          data: payload,
        };
      },
      invalidatesTags: ["USER"],
    }),

    editUser: builder.mutation({
      query: (payload: any) => {
        const { userId, ...params } = payload;
        return {
          url: `/user/${userId}`,
          method: "PUT",
          data: params,
        };
      },
    }),

    userLogin: builder.mutation<any, { email: string; password: string }>({
      query: (payload: any) => ({
        url: "/user/login",
        method: "POST",
        data: payload,
      }),
    }),

    updateUserStatus: builder.mutation<
      updateUserStatusPayload,
      { id: number; payload: any }
    >({
      query: ({ id, payload }) => ({
        url: `/user/${id}`,
        method: "PATCH",
        data: payload,
      }),
      invalidatesTags: ["USER"],
    }),

    resetPasswordAction: builder.mutation<
      ResetPasswordResponse,
      ResetPasswordRequest
    >({
      query: ({ password, confirmPassword, resetToken }) => ({
        url: `/user/reset-password/${resetToken}`,
        method: "PUT",
        data: { confirmPassword, password },
      }),
    }),

    forgotPwdEmailAction: builder.mutation<
      ForgotPasswordResponse,
      { email: string; portalType: string }
    >({
      query: (payload) => ({
        url: `/user/reset-password`,
        method: "POST",
        data: payload,
      }),
    }),
    //Delete Role data
    deleteRole: builder.mutation<void, { roleId: number; payload?: any }>({
      query: ({ roleId, payload }) => ({
        url: `/role/${roleId}`,
        method: "DELETE",
        data: payload,
      }),
      invalidatesTags: ["ROLE"], // Adjust tags if necessary
    }),

    updateRole: builder.mutation({
      query: ({ payload, roleId }) => ({
        url: `/role/${roleId}`,
        method: "PUT",
        data: payload,
      }),
      invalidatesTags: ["ROLE"], // Adjust tags if necessary
    }),
   
  }),
});
// delete Role data

export const {
  useSendOtpMutation, // sendOtp
  useVerifyOtpMutation, // verifyOtp
  useFetchedUsersQuery, //fetchedUsers
  useCreateUserMutation, //userRoles
  useEditUserMutation, //createUser
  useUserRolesQuery, //editUser userRole
  useUserProgramQuery, //list
  useUserLoginMutation, //userLogin
  useUpdateUserStatusMutation, //updateUserStatus //userLogin
  useResetPasswordActionMutation, // Reset Password
  useListOfProgramsQuery, //Fetch List of Programs
  useListOfProjectsQuery, //Fetch List of Project in usermanagements
  useCreateProjectMutation,
  useEditProjectMutation,
  useDeleteProjectMutation,
  useForgotPwdEmailActionMutation, //Fetch List of Programs
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useLazyGetRolesByIdQuery,
  useUpdateRoleMutation,
} = userApi;
